import { resetFilters } from '../../filters/ad_insertion';
import Events, { EventTypes } from '../../lib/events';
import Logger from '../../lib/logger';
import { clone, extend } from '../../lib/utils';
import Constants from '../../lib/constants';
import {
  DisplayGateKeys,
  DEFAULT_FIRST_SLOT_TIMEOUT,
  DEFAULT_FIRST_SLOT_RESPONSE_TIMEOUT,
} from '../../lib/display_gate_keys';
import { bootDependenciesOfSettings } from '../../lib/settings_dependencies';
import settings from '../../lib/settings';
import Slot from '../../lib/slot';
import GamAdSlot from '../../lib/slots/gam_ad_slot';
import CivicScienceSlot from '../../lib/slots/civic_science_slot';
import ConcertPreviewSlot from '../../lib/slots/concert_preview_slot';
import ConnatixSlot from '../../lib/slots/connatix_slot';
import ReverbSlot from '../../lib/slots/reverb_slot';
import MetabetSlot from '../../lib/slots/metabet_slot';
import OpenWebSlot from '../../lib/slots/open_web_slot';
import OutbrainSlot from '../../lib/slots/outbrain_slot';
import XcoSlot from '../../lib/slots/xco_slot';
import BuyButtonSlot from '../../lib/slots/buy_button_slot';
import NodeResize from '../../lib/node_resize';
import ViewportResize from '../../lib/viewport_resize';
import { conditionExists } from '../../lib/utils';

export const SLOT_TYPES = [
  CivicScienceSlot,
  ConnatixSlot,
  GamAdSlot,
  MetabetSlot,
  OutbrainSlot,
  OpenWebSlot,
  XcoSlot,
  BuyButtonSlot,
  ReverbSlot,
];

// The first-slot gate default timings live with the gate keys (the display_control mixin needs them
// too); re-exported here, where they have always been imported from.
export { DEFAULT_FIRST_SLOT_TIMEOUT, DEFAULT_FIRST_SLOT_RESPONSE_TIMEOUT };

/**
 * Check if an element is within any excluded container
 * @param {Element} element - The element to check
 * @param {Array} excludedContainers - Array of selectors or DOM elements to exclude
 * @param {Set<Element>} excludedContainersSet - Pre-computed set of excluded container elements (optional, for performance)
 * @returns {Boolean} True if element is within an excluded container
 */
function isWithinExcludedContainer(element, excludedContainers, excludedContainersSet = null) {
  if (!excludedContainers || excludedContainers.length === 0) {
    return false;
  }

  // Use cached set if provided (performance optimization)
  if (excludedContainersSet) {
    // Check if element is contained by any excluded container using Set iteration
    for (const excludedElement of excludedContainersSet) {
      if (excludedElement.contains(element)) {
        return true;
      }
    }
    return false;
  }

  // Fallback to original logic if set not provided
  return excludedContainers.some(exclusion => {
    // If it's a selector string, find matching elements
    if (typeof exclusion === 'string') {
      return Array.from(document.querySelectorAll(exclusion)).some(excludedElement =>
        excludedElement.contains(element)
      );
    }
    // If it's a DOM element, check directly
    if (exclusion && exclusion.nodeType === Node.ELEMENT_NODE) {
      return exclusion.contains(element);
    }
    return false;
  });
}

class Installer {
  constructor(app, dom, timesInserted) {
    this.app = app;
    this.dom = dom || document;
    this.app.dom = this.dom;

    /**
     * Keep track of how many times a slot is inserted, in case there's a maxSlots
     * filter set on the slot.
     * @type {Object}
     */
    this.timesInserted = timesInserted || {};

    /**
     * Cache for excluded container elements to avoid repeated DOM queries
     * @type {Set<Element>|null}
     */
    this._excludedContainersCache = null;
    this._excludedContainersCacheKey = null;
  }

  /**
   * Get cached excluded container elements as a Set for O(1) lookups
   * @param {Array} excludedContainers - Array of selectors or DOM elements
   * @returns {Set<Element>} Set of excluded container elements
   */
  _getExcludedContainersSet(excludedContainers) {
    if (!excludedContainers || excludedContainers.length === 0) {
      return new Set();
    }

    // Create cache key from excluded containers
    const cacheKey = excludedContainers
      .map(ex => (typeof ex === 'string' ? ex : ex?.id || ex?.className || 'element'))
      .join('|');

    // Return cached set if available
    if (this._excludedContainersCache && this._excludedContainersCacheKey === cacheKey) {
      return this._excludedContainersCache;
    }

    // Build set of excluded container elements
    const excludedSet = new Set();
    excludedContainers.forEach(exclusion => {
      if (typeof exclusion === 'string') {
        // Query once and add all matching elements to set
        document.querySelectorAll(exclusion).forEach(el => excludedSet.add(el));
      } else if (exclusion && exclusion.nodeType === Node.ELEMENT_NODE) {
        excludedSet.add(exclusion);
      }
    });

    // Cache the result
    this._excludedContainersCache = excludedSet;
    this._excludedContainersCacheKey = cacheKey;

    return excludedSet;
  }

  insertSlot(dynamicConfig) {
    const rawConfig = clone(dynamicConfig);
    // Pass this.dom (scoped DOM) to condition check so it can detect boost container vs parent page
    let config = updateConfigBasedOnConditionl(rawConfig, this.app.settings, this.dom);

    Logger.log('Using dynamic rule: ' + config.name + ' - ' + config.selector);

    if (configInvalidatedByViewport(config)) {
      Logger.log(`Skipping dynamic rule: ${config.name} - ${config.selector} because of a viewport filter`);
      return;
    }

    // Pre-compute excluded containers set once for all neighbors (performance optimization)
    const excludedContainers = this.app.settings.excludedContainers;
    const excludedContainersSet = this._getExcludedContainersSet(excludedContainers);

    // Check if scoped DOM is itself an excluded container (cached lookup)
    const isScopedToExcludedContainer = excludedContainersSet.has(this.dom);

    // Since NodeList.forEach is not supported in all browsers yet, we're borrowing
    // Array.prototype's forEach method to iterate over each node in the NodeList
    [...this.dom.querySelectorAll(config.selector)].forEach(neighbor => {
      // Check if this insertion point is within an excluded container
      // Skip this check if the scoped DOM (this.dom) is itself an excluded container,
      // allowing reinstall() to insert ads within excluded containers when explicitly scoped
      if (
        !isScopedToExcludedContainer &&
        isWithinExcludedContainer(neighbor, excludedContainers, excludedContainersSet)
      ) {
        Logger.log(`${config.name} wasn't rendered because insertion point is within an excluded container`);
        return false;
      }

      // create a local, mutable copy of the config for this slot instance
      let slotConfig = clone(config);

      if (slotConfig.maxSlots && this.timesInserted[slotConfig.name] >= slotConfig.maxSlots) {
        Logger.log(`${slotConfig.name} wasn't rendered because maxSlots of ${slotConfig.maxSlots} reached`);
        return false;
      }

      if (!Slot.filterSlot({ config: slotConfig, el: neighbor, settings: this.app.settings, scopedDom: this.dom })) {
        return false;
      }

      const SlotClass = Constants.CONCERT_PREVIEW ? ConcertPreviewSlot : SLOT_TYPES.find(s => s.isSlotTypeFor(config));

      /**
       * Responsible for determining if this slot config can be inserted or not
       */
      if (SlotClass.shouldSkip(this, slotConfig, neighbor, this.app.settings)) return false;

      if (!this.app.slots) return;

      // Check if a slot already exists at this location to prevent duplicate insertions during reinstall
      // This prevents parent ads from being re-inserted when reinstall() is called with uninstall: false
      const existingSlotAtLocation = this.app.slots.all().find(slot => {
        return slot.neighbor === neighbor && slot.configName === slotConfig.name;
      });

      if (existingSlotAtLocation) {
        Logger.log(`${slotConfig.name} wasn't rendered because a slot already exists at this location`);
        return false;
      }

      const slot = new SlotClass(
        this.app,
        extend(
          {
            index: this.timesInserted[slotConfig.name] || 0,
          },
          slotConfig
        ),
        neighbor
      );

      // Fixing a bug with Ad Block
      if (!this.app.slots) {
        return;
      }

      this.app.slots.push(slot);

      // Consider this slot for first-slot designation (and the response gate's countdown) before
      // inserted() runs (via insertNextTo).
      this.app.considerSlotForGates(slot);

      var wrapper = slot.getSlotWrapper({ slot, config: slotConfig });

      if (slotConfig.filters?.loadAfter?.event) {
        isLoadedAfterAnEvent(slotConfig, neighbor, wrapper);
      } else {
        wrapper.insertNextTo(neighbor);
      }

      incrementSlotCount.call(this, slotConfig);

      resetFilters();
    });
  }
}

function updateConfigBasedOnConditionl(config, settings, scopedDom = null) {
  const conditional = config.conditional;

  if (!conditional) {
    return config;
  }

  // Support array of conditionals - check each one in order, apply first match
  if (Array.isArray(conditional)) {
    for (let i = 0; i < conditional.length; i++) {
      const cond = conditional[i];
      if (conditionExists(cond, settings, scopedDom)) {
        return { ...config, ...cond };
      }
    }
    return config;
  }

  // Single conditional (backward compatible)
  return conditionExists(conditional, settings, scopedDom) ? { ...config, ...conditional } : config;
}

function isValidEvent(event, slotConfig) {
  if (event && EventTypes[event]) {
    return true;
  } else {
    Logger.warn(`${slotConfig.name} wasn't rendered because ${event} is not a valid event.`);
    return false;
  }
}

function isLoadedAfterAnEvent(slotConfig, neighbor, wrapper) {
  const event = slotConfig.filters?.loadAfter?.event;
  if (event && isValidEvent(event, slotConfig)) {
    Logger.log(`${slotConfig.name} is waiting for the ${event} to load`);
    Events.once(EventTypes[event], () => {
      wrapper.insertNextTo(neighbor);
    });
    return true;
  }
}

/**
 * Increment the number of times a slot is inserted. For DynamicAds usage only
 * to respect the maxSlots param set on the config.
 * @param  {object} config Dynamic config
 * @return {undefined}
 */
function incrementSlotCount(config) {
  this.timesInserted[config.name] = this.timesInserted[config.name] + 1 || 1;
}

/**
 * Check to see if config is invalidated by the current viewport filter.
 * @param {Object} config
 */
function configInvalidatedByViewport(config) {
  if (config.filters && config.filters.viewportWidth) {
    let filter = config.filters.viewportWidth;
    delete config.filters.viewportWidth;

    return !Slot.filterSlot({
      config: {
        name: config.name,
        filters: {
          viewportWidth: filter,
        },
      },
      el: null,
      settings: null,
      scopedDom: document,
    });
  }

  return false;
}

export function installMixin(ConcertAds) {
  /**
   * Install Concert Ads
   * @param {Document} dom
   * @param {Boolean} skipBootDependencies  skip re-booting settings dependencies (reinstall passes
   *   its preserveTargeting flag here to keep existing page-level targeting)
   * @param {Boolean} rearmFirstSlotGate  whether to (re)arm the firstSlot display gate for a fresh
   *   cycle. Defaults true for a normal install and a reinstall that tore its slots down; reinstall
   *   forwards its `uninstall` flag so a reinstall({ uninstall: false }) — which keeps existing slots —
   *   does NOT re-arm (a fresh gate would hold the surviving page behind it).
   * @returns null
   */
  ConcertAds.prototype.install = function(dom, skipBootDependencies = false, rearmFirstSlotGate = true) {
    if (document.querySelector('#money_pixel_page_level_exception')) {
      return false;
    }

    if (window.disableConcertAdsOnThisPage === true) {
      if (console && console.warn) {
        console.warn(
          "Concert Ads has been disabled on this page, no ads will be shown. I sure hope you know what you're doing"
        );
      } else {
        Logger.warn(
          "Concert Ads has been disabled on this page, no ads will be shown. I sure hope you know what you're doing"
        );
      }
      return false;
    }

    if (!skipBootDependencies) {
      bootDependenciesOfSettings(this);
    }

    // Auto-arm the firstSlot display gate. Done after settings dependencies have booted (so it reads
    // the fully resolved settings — a stale waitForFirstSlot would fail to arm at all, a stale
    // firstSlotTimeout would schedule the backstop with the wrong delay) and before any slot renders
    // (slots are inserted below), so both settings.slots first-slot candidates and later
    // registerSlot({ firstSlot: true }) calls are covered.
    //
    // This automatic mechanism is coupled to auto-release: `waitForFirstSlot` turns it on (arm here),
    // while `autoReleaseFirstSlot` governs whether Concert releases the gate on the first slot's own
    // render (Slot#releaseFirstSlotHold), or a caller owns releaseDisplay('firstSlot'). (finalizeSlots()
    // also opens it once every slot is declared — see that method.)
    //
    // install() re-runs on reinstall(), so this re-fires for SPA navigation: drop any stale gate from
    // the prior cycle so each fresh (re)install starts a new cycle. blockUntilDisplay() no-ops on an
    // already-armed key, so the delete is what guarantees the re-arm actually takes on reinstall.
    // N=1 designation is derived from the slots themselves (isFirstSlot()), so there is no separate
    // flag to reset here.
    //
    // rearmFirstSlotGate is false only for reinstall({ uninstall: false }): the existing slots (and
    // any first slot among them) survive that reinstall, so re-arming would hold the still-live page
    // behind a brand-new gate. Leave the prior cycle's gate state untouched in that case.
    //
    // The firstSlotResponse gate shares the firstSlot cycle but is NOT armed here. It holds only the
    // slots flagged `waitForFirstSlotResponse` until the first slot's GAM response renders
    // (Slot#releaseFirstSlotResponseHold) or its backstop fires, so it is armed when such a slot
    // registers (ConcertAds#armFirstSlotResponseGate, from considerSlotForGates) — and armed again by any
    // later one that registers while the first slot has still not answered. A page with no such slot
    // never arms it or runs its timer. Here we only drop the prior cycle's gate so a reinstall starts
    // clean. Its backstop is `firstSlotResponseTimeout` from arming (renewed once if the first slot is
    // still on its way — see extendFirstSlotResponseOnTimeout), re-anchored to the first slot's request
    // (Slot#firstSlotRequested), so that setting budgets only GAM's answer, not the bid auction before
    // it. `firstSlot`, by contrast, ticks from here: a first slot is expected, so it is timed from
    // install.
    if (rearmFirstSlotGate && this.settings.waitForFirstSlot) {
      this.displayGates.delete(DisplayGateKeys.FIRST_SLOT);
      this.blockUntilDisplay(DisplayGateKeys.FIRST_SLOT, {
        timeout: this.settings.firstSlotTimeout ?? DEFAULT_FIRST_SLOT_TIMEOUT,
      });

      this.displayGates.delete(DisplayGateKeys.FIRST_SLOT_RESPONSE);

      // A new cycle: finalizeSlots() has not run yet, so an external display gate that is already
      // pending (armed by the page before install) holds the page, and the firstSlot backstop that just
      // started must not run down behind it. It restarts when the last external gate opens.
      this.finalizedExternalGates = new WeakSet();
      if (this.hasExternalDisplayHold()) {
        this.pauseFirstSlotTimers();
      }
    }

    // Reset timesInserted when install() is called to reset maxSlots counter
    this.installer = new Installer(this, dom, {});
    Events.emit(EventTypes.installing);

    if (!this.settings.slug) {
      Logger.warn("You are missing a 'slug' config, which is required to render Google-served ads. Please add one.");
    }
    Logger.log(`The GAM slug being used is ${this.settings.slug}`);

    // We don't want these set up again on reinstall
    if (!this.hasReinstalled) {
      if (this.settings.nodeResize) new NodeResize(this);
      if (this.settings.viewportResize) new ViewportResize(this);
    }

    this.settings.slots.forEach(config => {
      try {
        this.installer.insertSlot(config);
      } catch (error) {
        Logger.log(`There was an error installing the slot ${error} for config: ${config}`);
      }
    });

    Events.emit(EventTypes.slotsInserted);

    if (this.settings.prebid.biddingEnabled) {
      this.bidManager.init();
    }

    // Convenience for pages that declare every slot through install() itself. This runs
    // synchronously, so it is deliberately NOT the right trigger when you want a meaningful
    // firstSlot gate: a page that registers slots after install, or that wants the pack held until
    // the first slot's auction returns, should leave this off and call finalizeSlots() itself. See
    // the corollary on ConcertAds#finalizeSlots in display_control.js.
    if (this.settings.autoFinalizeSlots) {
      this.finalizeSlots();
    }
  };

  /**
   * Re-install slots in a given area on the page. Defaults to entire document.
   *
   * @param {DOM Node} dom
   * @param {Object} updatedSettings - Settings to merge with existing settings
   * @param {Object} slotTargeting - Targeting to apply to newly created slots
   * @param {Boolean} uninstall - Whether to uninstall existing slots first
   * @param {Boolean} preserveTargeting - If true, don't reset page-level targeting
   * @param {Boolean} enableDestroyOnReinstall - If true, destroy slots marked with destroyOnReinstall flag
   */
  ConcertAds.prototype.reinstall = function(
    dom,
    updatedSettings,
    slotTargeting,
    uninstall = true,
    preserveTargeting = false,
    enableDestroyOnReinstall = false
  ) {
    Logger.log('ConcertAds Reinstall called with params', {
      dom,
      updatedSettings,
      slotTargeting,
      uninstall,
      preserveTargeting,
      enableDestroyOnReinstall,
    });

    if (uninstall) {
      this.uninstall();
    } else if (enableDestroyOnReinstall) {
      // Auto-destroy slots marked with destroyOnReinstall in their config
      const slotNamesToDestroy = this.settings.slots
        .filter(slotConfig => slotConfig.destroyOnReinstall === true)
        .map(slotConfig => slotConfig.name);

      if (slotNamesToDestroy.length > 0) {
        const slotsToDestroy = this.slots.all().filter(slot => slotNamesToDestroy.includes(slot.name));
        Logger.log(`ConcertAds: Auto-destroying ${slotsToDestroy.length} slots marked with destroyOnReinstall`, {
          slotNames: slotsToDestroy.map(s => s.name),
        });
        slotsToDestroy.forEach(slot => {
          slot.destroy();
          this.slots.slots.delete(slot.name);
        });
      }
    }

    this.hasReinstalled = true;

    if (this.settings.prebid.biddingEnabled) {
      this.bidManager.disable();
    }

    // Resetting any new settings that are passed in
    // but leaving what is already there in place
    if (updatedSettings && typeof updatedSettings === 'object' && Object.keys(updatedSettings).length > 0) {
      settings.set({
        ...settings.get(),
        ...updatedSettings,
      });
    }

    // Track which slot instances exist before this reinstall (by object reference, not name)
    const existingSlots = new Set(this.slots.all());
    Logger.log('ConcertAds Reinstall: Tracking existing slot instances', {
      count: existingSlots.size,
    });
    Logger.log('ConcertAds Reinstall: slotTargeting to apply', { slotTargeting });

    // Pass skipBootDependencies=true (preserveTargeting) to keep existing targeting. Forward
    // `uninstall` as rearmFirstSlotGate so the firstSlot gate re-arms only when we tore the old slots
    // down; a reinstall that keeps its slots (uninstall:false) must not re-arm (see install()).
    this.install(dom, preserveTargeting, uninstall);

    // Apply slot-level targeting to newly created slots AFTER install completes
    if (slotTargeting && typeof slotTargeting === 'object') {
      const allSlots = this.slots.all();
      const newSlots = allSlots.filter(slot => !existingSlots.has(slot));

      Logger.log('ConcertAds: Found new slots after install', {
        count: newSlots.length,
        slotNames: newSlots.map(s => s.name),
      });

      newSlots.forEach(slot => {
        if (slot.setTargeting) {
          Logger.log(`ConcertAds ✓ Applying targeting to NEW slot: ${slot.name}`, { slotTargeting });
          Object.keys(slotTargeting).forEach(key => {
            slot.setTargeting(key, slotTargeting[key]);
          });
        } else {
          Logger.log(`ConcertAds ✗ Slot ${slot.name} does not have setTargeting method`);
        }
      });
    }
  };

  /**
   * Uninstall slots from the page.
   */
  ConcertAds.prototype.uninstall = function() {
    // Empty the registry BEFORE destroying. Destroying the first slot opens the first-slot gates, which
    // run hooks (the special-slot sweep) over the slots still registered; with the registry already
    // cleared those hooks see every slot as gone instead of force-requesting ones about to be destroyed.
    const slots = this.slots.all();
    this.slots.clear();
    slots.forEach(slot => slot.destroy());
  };

  /**
   * Insert slots in a given area on the page. Defaults to all slots and entire document.
   *
   * @param {String} domSelector
   * @param {Array} includedSlots
   * @param {Array} excludedSlots
   */
  ConcertAds.prototype.insertSlots = function({ domSelector, includedSlots = [], excludedSlots = [] } = {}) {
    const dom = document.querySelector(domSelector) ? document.querySelector(domSelector) : document;
    const slots = includedSlots.length
      ? this.settings.slots.filter(slot => includedSlots.includes(slot.name))
      : this.settings.slots.filter(slot => !excludedSlots.includes(slot.name));

    // We don't want to install slots until Concert Ads initial install on the page
    if (!this.installer) return;

    this.installer = new Installer(this, dom, this.installer.timesInserted);
    slots.forEach(config => {
      if (config.insertSlot === false && !includedSlots.includes(config.name)) return;
      this.installer.insertSlot(config);
    });
  };
}
